/*
  PDP — Piece behaviours.

  Two custom elements, both optional: with the script blocked the section still
  reads, it just loses the motion and the enlargement.

    <v2-pdp-clips>        the detail clips, attached on approach
    <v2-pdp-piece-video>  the hero thumbnail and its lightbox

  ---------- Where this came from ----------

  <v2-pdp-clips> is ported from <travel-bundle-clips> in assets/travel-bundle.js.
  It is the driver snippets/v2-pdp-media.liquid's `managed` branch has always
  emitted markup for and the PDP fork never had — which is why that snippet used
  to carry a note telling callers not to pass `managed`. That note is now gone,
  because this file is the thing it said was missing.

  <v2-pdp-piece-video> is the same shape as the fit-video dialog in
  assets/v2-pdp-size-fit.js: one native <dialog>, opened from a thumbnail,
  source attached on open and torn down on close.

  A deliberate copy rather than a shared import, on the same terms as the rest
  of the fork: the travel bundle is paid cold traffic and must not move when a
  PDP is edited. A bug fixed in one is NOT fixed in the other.
*/

(function () {
  'use strict';

  /* ------------------------------------------------------------ detail clips */

  /**
   * Ambient clips that attach their own source as they approach the viewport.
   *
   * Without this every clip in the section downloads on page load: the
   * unmanaged branch of v2-pdp-media renders a real `src` plus `autoplay`, and
   * a muted looping video plays as soon as it can, which means fetching the
   * whole file. Four detail clips here plus four in the benefits section below
   * is eight videos racing the page.
   */
  class PdpClips extends HTMLElement {
    connectedCallback() {
      this.clips = Array.from(this.querySelectorAll('video[data-src]'));
      if (this.clips.length === 0) return;

      // Reduced motion: attach the source so the first frame stands in for the
      // clip, but never play it, and hand over native controls so it is still
      // watchable.
      this.manual = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Without IntersectionObserver there is no signal to attach on, and a
      // detail row showing a permanently empty box is worse than an eager
      // download. Attach everything and let the browser decide.
      if (!window.IntersectionObserver) {
        this.clips.forEach((clip) => this.attach(clip));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => this.onIntersect(entry)),
        {
          // A screen of lead time, so the clip has started arriving by the time
          // it is looked at. Attaching exactly at the viewport edge trades an
          // eager download for a visible wait, which is not the trade intended.
          rootMargin: '400px 0px',
          threshold: 0
        }
      );

      this.clips.forEach((clip) => this.observer.observe(clip));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    onIntersect(entry) {
      const clip = entry.target;

      if (!entry.isIntersecting) {
        // Paused rather than torn down. The bytes are already spent, so
        // dropping the source would only mean paying for them twice on the way
        // back up; what pausing saves is the decode, which runs for as long as
        // a looping clip is playing whether or not anyone is watching.
        if (!clip.paused) clip.pause();
        return;
      }

      this.attach(clip);
      if (!this.manual) this.play(clip);
    }

    attach(clip) {
      const src = clip.dataset.src;
      if (!src) return;

      // Removed first, so this is a one-way door: the observer fires again on
      // every crossing and re-setting src would restart the download.
      delete clip.dataset.src;

      // Metadata, not none, now that a source exists. The element still has to
      // paint a first frame, and preload="none" would leave it blank until
      // play() — which never comes under reduced motion.
      clip.preload = 'metadata';
      clip.src = src;

      if (this.manual) clip.controls = true;
    }

    /**
     * Autoplay can still be refused — a battery-saver mode will reject it even
     * for a muted clip — and the rejection arrives as a promise. Swallowed
     * rather than handled: the frame is already on screen, so the failure state
     * is a still image of the thing the row is describing.
     */
    play(clip) {
      clip.play()?.catch(() => {});
    }
  }

  /* ----------------------------------------------------------- hero lightbox */

  /**
   * A poster thumbnail that opens the full clip in a native <dialog>.
   *
   * Why a lightbox rather than an inline autoplaying clip: the hero video is a
   * finished piece of film, long and heavy, not the three-second ambient loops
   * the detail rows use. Inline and muted it would be ignored; inline and
   * audible it would be an ambush. Behind a play button it is something she
   * chooses, which is also the only honest way to spend the bytes.
   *
   * The source is attached on open and removed on close, so a visitor who never
   * presses play never downloads a frame. That is load-bearing rather than
   * tidy — see the note on file size in sections/v2-pdp-piece.liquid.
   */
  class PdpPieceVideo extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-piece-play]');
      this.dialog = this.querySelector('[data-piece-dialog]');
      this.video = this.querySelector('[data-piece-video]');
      this.closeButton = this.querySelector('[data-piece-close]');

      // No <dialog> support, or markup missing: leave the trigger inert rather
      // than half-wiring it. showModal() would throw and the click would look
      // broken; doing nothing at least leaves a poster of the jacket.
      if (!this.trigger || !this.dialog || !this.video) return;
      if (typeof this.dialog.showModal !== 'function') return;

      this._onOpen = this.open.bind(this);
      this._onClose = this.close.bind(this);
      this._onDialogClose = this.teardown.bind(this);
      this._onBackdrop = this.onBackdrop.bind(this);

      this.trigger.addEventListener('click', this._onOpen);
      this.closeButton?.addEventListener('click', this._onClose);

      // `close` rather than the button alone — Escape and the backdrop both
      // dismiss a native <dialog>, and all three have to stop the video.
      this.dialog.addEventListener('close', this._onDialogClose);
      this.dialog.addEventListener('click', this._onBackdrop);

      this.trigger.hidden = false;
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this._onOpen);
      this.closeButton?.removeEventListener('click', this._onClose);
      this.dialog?.removeEventListener('close', this._onDialogClose);
      this.dialog?.removeEventListener('click', this._onBackdrop);
    }

    open() {
      const src = this.dataset.videoSrc;
      if (!src) return;

      // Only on first open. Re-setting src on every open would restart the
      // download for someone who closed the dialog and opened it again.
      if (this.video.getAttribute('src') !== src) {
        this.video.setAttribute('src', src);
      }

      this.dialog.showModal();

      // Autoplay can be refused; the dialog is open either way and the controls
      // are there, so the failure state is a video waiting to be pressed.
      this.video.play()?.catch(() => {});
    }

    close() {
      this.dialog?.close();
    }

    /**
     * Stop playback however the dialog was dismissed. Without this, closing on
     * Escape leaves the audio running behind the page.
     */
    teardown() {
      this.video.pause();
    }

    /**
     * Clicks land on the dialog element itself only when they miss the inner
     * panel — which is the backdrop.
     */
    onBackdrop(event) {
      if (event.target === this.dialog) this.close();
    }
  }

  if (!window.customElements.get('v2-pdp-clips')) {
    window.customElements.define('v2-pdp-clips', PdpClips);
  }

  if (!window.customElements.get('v2-pdp-piece-video')) {
    window.customElements.define('v2-pdp-piece-video', PdpPieceVideo);
  }
})();
